import { useState, useEffect, useRef, Fragment } from "react";
import { Grid, Divider, DialogActions, DialogContent } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import BaseDialog from "../../../common/base-dialog/base-dialog";

//import styles
import "./rating.scss";

function Rating({ min, max, value, step, onChange, active, submitForm }) {
    //general variables
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    
    //define emoji and name lists for rating
    const emoji = ["soGood", "good", "normal", "bad", "soBad", "noPoint"];
    const nameList = [
        t("General.Very Good"),
        t("General.Good"),
        t("General.Average"),
        t("General.Bad"),
        t("General.Very Bad"),
        t("General.No Opinion"),
    ];
    
    //define questions for the rating system
    const questionList = [
        { label: t("matchingPreferences"), name: "taste_matching" },
        { label: t("diversity"), name: "variety" },
        { label: t("locationDistances"), name: "location_distances" },
    ];

    //state to manage dialog open status
    const [open, setOpen] = useState(false);

    //handle close the modal and update search parameters
    const handleClose = () => {
        setOpen(false);
        searchParams.delete("action");
        setSearchParams(searchParams, { replace: true });
    };

    // Effect to open modal based on search parameter
    useEffect(() => {
        if (searchParams.get("action") === "rating") setOpen(true);
        else setOpen(false);
    }, [searchParams]);

    //state to manage range values for each question
    const [rangeValue, setRangeValue] = useState({ taste_matching: 0, variety: 0, location_distances: 0 });
    const rangeRef = useRef(null);

    // Effect to update range values when the value prop changes
    useEffect(() => {
        if (value) {
            setRangeValue(value);
        }
    }, [value]);

    //handle change in range input
    const handleChange = (e, newName) => {
        e.preventDefault();
        const newVal = +e.target.value;
        if (!value) setRangeValue(values => ({ ...values, [newName]: newVal }));
        onChange(newVal, newName);
    };

    //change input value based on click position
    const changeInputValue = (e, newName) => {
        let bounds = rangeRef.current.getBoundingClientRect();
        let x = e.clientX - bounds.left - 10;
        const percent = Math.round((x * 100) / bounds.width);
        const newValue = Math.ceil(max * (percent / 100));
        setRangeValue(values => ({ ...values, [newName]: newValue }));
        onChange(newValue, newName);
    };

    //calculate position of value indicator for each question
    const valuePosition = questionList.map(questionItem => {
        return ((rangeValue?.[questionItem?.name] - min) / (max - min)) * 100;
    });

    //calculate number of separators for the range input
    const separatorSize = parseInt(max) / parseInt(step);

    return (
        <BaseDialog title={t("rating")} open={open} handleClose={handleClose} maxWidth="md" top="0" icon="close">
            <DialogContent dividers={true}>
                <Grid className="rating__title" component="span" container item xs={11}>
                    {t("ratingMessage")}
                </Grid>

                <Grid className="rating__content" container item xs={12} rowGap={{ xs: 2, md: 6 }}>
                    {questionList?.map((questionItem, index) => {
                        return (
                            <Fragment key={index}>
                                <Grid container item xs={12} justifyContent={{ xs: "center", md: "space-between" }} rowGap={{ xs: 2, md: 0 }}>
                                    <Grid className="rating__rating-label" container item xs={12} md={2.5} alignItems="center">
                                        {questionItem.label}
                                    </Grid>

                                    <Grid container item xs={10} md={9}>
                                        <div className="rating__rating-wrapper">
                                            <span className="rating__label" style={{ left: `${valuePosition[index] - 7}%` }}>
                                                {nameList[rangeValue?.[questionItem?.name]]}
                                            </span>
                                            <div className="rating__input-wrapper">
                                                <input
                                                    className="rating__input"
                                                    type="range"
                                                    value={rangeValue?.[questionItem?.name]}
                                                    min={min}
                                                    max={max}
                                                    step={step}
                                                    onChange={e => handleChange(e, questionItem?.name)}
                                                />
                                            </div>
                                            <div className="rating__control-wrapper">
                                                <div
                                                    className={`rating__control ${emoji[rangeValue?.[questionItem?.name]]}`}
                                                    style={{ left: `${valuePosition[index] - 4}%` }}
                                                />
                                                <div
                                                    className="rating__rail"
                                                    onClick={active ? e => changeInputValue(e, questionItem?.name) : ""}
                                                    ref={rangeRef}
                                                >
                                                    {Array.from({ length: separatorSize + 1 }, (_, i) => (
                                                        <span
                                                            className="rating__rail--separator"
                                                            style={{ position: "absolute", left: `${i * (100 / max)}%`, top: "4px" }}
                                                            key={i}
                                                        ></span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </Grid>
                                </Grid>
                                <Divider width="100%" sx={{ borderBottomColor: "var(--light-gray-color)" }} />
                            </Fragment>
                        );
                    })}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container item xs={11} md={4}>
                    <BaseButton text={t("submit")} color="primary" size="medium" radius="radius-10" handleClick={submitForm} />
                </Grid>
            </DialogActions>
        </BaseDialog>
    );
}

export default Rating;
